import {Injectable} from "@angular/core";
import {AddAssetsDialogModel} from "../workflow/site/add-assets-dialog.model";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {EditAssetDialogComponent, EditAssetDialogModel} from "./edit-asset-dialog.component";
import {ModifyContactDialogComponent} from "../contact/modify-contact-dialog.component";
import {ContactService} from "../contact/contact.service";
import {ContactModel} from "../contact/contact.model";
import {HttpClient} from "@angular/common/http";
import {map} from "rxjs/operators";
import {Apollo} from "apollo-angular";
import {exposePropertyInDebugMode} from "../misc/expose-item-in-debug-mode.function";
import {AssetGqlModel, AssetInputGqlModel, AssetInputResponseGqlModel} from "./asset.gql-model";
import {RemoveAssetsDialogModel} from "../workflow/site/remove-assets-dialog.model";
import {ModifyAddressDialogComponent} from "../address/modify-address-dialog.component";
import {AddressModel} from "../address/address.model";

@Injectable()
export class AssetService {
  private url: string = "/api/assets";

  constructor(
    private http: HttpClient,
    private apollo: Apollo,
    private dialogService: DialogService,
    private contactService: ContactService,
  ) {
    exposePropertyInDebugMode(
      "getAsset",
      {
        enumerable: true,
        configurable: true,
        get: () => this.getAsset.bind(this),
      },
    );
  }

  async deleteById(id: string): Promise<boolean> {
    if (!id || typeof id !== "string") throw new Error("No asset selected for removal");

    return this.http.get<{success: boolean}>(`${this.url}/remove`, {params: {id}})
      .pipe(map(json => !!json.success))
      .toPromise();
  }

  async batchCreate(params: AddAssetsDialogModel): Promise<boolean> {
    if (!params.siteId) throw new Error("No site selected");

    return this.http.post<{success: boolean}>(`${this.url}/batch-create`, params)
      .pipe(map(json => !!json.success))
      .toPromise();
  }

  async createRemoval(params: RemoveAssetsDialogModel): Promise<boolean> {
    if (!params.siteId) throw new Error("No site selected");

    return this.http.post<{success: boolean}>(`${this.url}/create-removal`, params)
      .pipe(map(json => !!json.success))
      .toPromise();
  }

  updateAssetsForOrder(orderId: string, assetIds: string[], accessoryIds: string[]): Promise<boolean> {
    return this.http.post<{success: boolean}>(`${this.url}/update-assets-for-order`, {orderId, assetIds, accessoryIds})
      .pipe(map(json => !!json.success))
      .toPromise();
  }

  updateAssetsForDeployment(deploymentId: string, assetIds: string[], accessoryIds: string[]): Promise<boolean> {
    return this.http.post<{success: boolean}>(
      `${this.url}/update-assets-for-deployment`,
      {deploymentId, assetIds, accessoryIds},
    )
      .pipe(map(json => !!json.success))
      .toPromise();
  }

  async checkIfAssetsAreOrdered(orderId: string): Promise<{ordered: number, selected: number}> {
    if (!orderId || typeof orderId !== "string") throw new Error("No order selected");
    const params = {orderId};

    return this.http.get<{ordered: number, selected: number}>(`${this.url}/check-if-assets-are-ordered`, {params})
      .pipe(map(({ordered, selected}) => ({ordered: +ordered, selected: +selected})))
      .toPromise();
  }

  getAssetsPicker(params: {[key: string]: string | undefined}): Promise<{rows: any[], count: number}> {
    for (const [k, v] of Object.entries(params)) {
      if (!v) delete params[k];
    }

    return this.http.get<{rows: any[], count: number}>(`${this.url}/picker`, {params})
      .pipe(map(({rows, count}) => ({rows, count: +count})))
      .toPromise();
  }

  getAsset(assetId: string): Promise<AssetGqlModel> {
    const query = AssetGqlModel.query;
    const variables = AssetGqlModel.variables(assetId);

    return this.apollo.query({query, variables, fetchPolicy: "no-cache"})
      .pipe(AssetGqlModel.mapper)
      .toPromise();
  }

  getDispositions(): Promise<any> {
    return this.http.get(`${this.url}/dispositions`)
      .toPromise();
  }

  async saveAsset(asset: AssetGqlModel): Promise<AssetInputResponseGqlModel> {
    const model = new AssetInputGqlModel({assetId: asset.id, asset});

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(AssetInputGqlModel.mapper)
      .toPromise();
  }

  editAsset(assetId: string): Promise<boolean> {
    return this.dialogService.showDialog(EditAssetDialogComponent, {assetId} as EditAssetDialogModel);
  }

  async editAssetContact(assetId: string, contactType: string): Promise<ContactModel> {
    const {contact} = await ModifyContactDialogComponent.showContactModal(
      this.dialogService,
      {
        contactService: this.contactService,
        targetType: contactType,
        targetLevel: "asset",
        targetId: assetId,
      },
    );

    return contact;
  }

  async editAssetAddress(assetId: string, addressType: string): Promise<AddressModel> {
    const {address} = await ModifyAddressDialogComponent.showAddressModal(
      this.dialogService,
      addressType,
      assetId,
    );

    return address;
  }
}
